/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box, Tooltip } from '@mui/material';
import isNil from 'lodash/isNil';
import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DetailsPanelEnum } from '../../../../shared/entities/detailsPanel';
import { ServiceTierRequestStatus } from '../../../../shared/entities/serviceTierRequest';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import { TerminalPlatformsEnum } from '../../../../shared/entities/terminalPlatform';
import { Authorities } from '../../../../shared/entities/user';
import {
  selectCurrentServiceTierChangeRequest,
  selectRequestingDeleteServiceTierChangeRequestError,
  selectUserData,
} from '../../../../store';
import {
  clearDeleteServiceTierChangeRequest,
  deleteServiceTierChangeRequest,
} from '../../../../store/serviceTierRequests/actions';
import { DetailsPanelGroupCollapseState } from '../../../../store/ui/reducer';
import { getConnectivityColor, isOnline } from '../../../../utils/ConnectionStatusUtils';
import { bytesToUnitString } from '../../../../utils/DataAmountUtils';
import { bpsToUnitString } from '../../../../utils/DataRateUtils';
import { SEC_TO_MS_CONVERSION } from '../../../../utils/DateTimeUtils';
import { isPlatformTypeInList } from '../../../../utils/PlatformTypeUtils';
import { areAllowedRolesPresent } from '../../../../utils/RBACUtils';
import {
  booleanToAliasOrPlaceholder,
  EMPTY_FIELD_STRING_PLACEHOLDER,
  formatSlaForDisplay,
  getTerminalTypeString,
  toFixedValueFormatter,
  transformNetworkName,
  transformRLWaveformName,
} from '../../../../utils/StringUtils';
import {
  getKaTerminalOnAircraft,
  getKuTerminalOnAircraft,
  getOtherTerminalOnAircraft,
  getSignalQuality,
  toCommaSeparatedString,
} from '../../../../utils/TerminalUtilities';
import CollapseGroup from '../../../common/collapse_group/CollapseGroup';
import CollapseGroupItem from '../../../common/collapse_group/CollapseGroupItem';
import ConnectivityCollapseGroupItem from '../../../common/collapse_group/ConnectivityCollapseGroupItem';
import { signalQualitySvg } from '../../TerminalIcons';
import LookAnglesContainerWrapper from '../LookAnglesPanel/LookAnglesContainerWrapper';
import NextSatellitePanelContainerWrapper from '../NextSatellitePanel/NextSatellitePanelContainerWrapper';
import SatellitesInFovContainer from '../SatellitesInFov/SatellitesInFovContainer';
import CancelServiceTierChangeRequestErrorDialog from './CancelServiceTierChangeRequestErrorDialog';
import ServiceTierChangeRequestButton from './ServiceTierChangeRequestButton';
import { calculateScrollbarHeightOffset, CollapseGroupScrollbarContainer } from './TerminalTabPanel';
import { FulltailViewButton } from './shared/FulltailViewButton';
import { FullVehicleViewButton } from './shared/FullVehicleViewButton';

interface TerminalTabDataDefaultProps extends TerminalTabDataDefaultStateProps, TerminalTabDataDefaultDispatchProps {}

export interface TerminalTabDataDefaultStateProps {
  detailsPanelVariant: DetailsPanelEnum;
  hidden: boolean;
  satellitesInFOVIndexOffset?: number;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
  isKuPlusKa: boolean;
  isDualTerminal: boolean;
  isOverviewMode: boolean;
  isFullTailViewMode: boolean;
  isNextSatellitePanelEnabled: boolean;
  isSatellitesInFOVEnabled: boolean;
  history: any;
  showDetailedConnectionStats: boolean;
}

export interface TerminalTabDataDefaultDispatchProps {
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
}

const HUB_NAME_TOOLTIP_MAX_WIDTH_PX = 124;
const SERVICE_REGION_NAME_TOOLTIP_MAX_WIDTH_PX = 262;
const MODEM_TOOLTIP_MAX_WIDTH_PX = 124;
const BEAM_TOOLTIP_MAX_WIDTH_PX = 262;
const ANTENNA_TOOLTIP_MAX_WIDTH_PX = 262;
const TAIL_DETAILS_TAIL_NUMBER_TOOLTIP_WIDTH_PX = 232;

export interface ScrollbarProps {
  scrollbarHeightOffset: number;
}

const TerminalTabDataDefault = ({
  detailsPanelVariant,
  hidden,
  tailDetailsPanelTerminal,
  satellitesInFOVIndexOffset = 0,
  isKuPlusKa,
  isDualTerminal,
  isOverviewMode,
  isNextSatellitePanelEnabled,
  isFullTailViewMode,
  detailsPanelGroupCollapseState,
  history,
  showDetailedConnectionStats,
  setDetailsPanelGroupCollapseState,
}: TerminalTabDataDefaultProps) => {
  // Local State
  const [highlightedSatelliteInFov, setHighlightedSatelliteInFov] = useState(undefined);
  // Selectors
  const userData = useSelector(selectUserData, shallowEqual);
  const currentServiceTierChangeRequest = useSelector(selectCurrentServiceTierChangeRequest, shallowEqual);

  const requestingDeleteServiceTierChangeRequestError = useSelector(
    selectRequestingDeleteServiceTierChangeRequestError,
    shallowEqual,
  );

  const dispatch = useDispatch();

  if (hidden) {
    return null;
  }

  function toggleGroupCollapsed(groupName: keyof DetailsPanelGroupCollapseState) {
    setDetailsPanelGroupCollapseState({
      ...detailsPanelGroupCollapseState,
      [groupName]: !detailsPanelGroupCollapseState[groupName],
    });
  }

  const terminalTypeString = getTerminalTypeString(tailDetailsPanelTerminal);

  const isOffline = isOverviewMode
    ? !isOnline(tailDetailsPanelTerminal?.connectivityStatus) &&
      !isOnline(getOtherTerminalOnAircraft(tailDetailsPanelTerminal).connectivityStatus)
    : !isOnline(tailDetailsPanelTerminal?.connectivityStatus);
  const fadedClassName = isOffline ? 'tail__details_faded_wrapper' : '';

  const isMultiTerminalAircraft = isDualTerminal || isKuPlusKa;
  const canManageServiceTier = areAllowedRolesPresent(
    userData,
    Authorities.ROLE_DEV_VIEW_ONLY,
    Authorities.ROLE_TDS_STCR_RW,
  );

  const scrollbarHeightOffset = calculateScrollbarHeightOffset(tailDetailsPanelTerminal, isOverviewMode);

  const terminalProps = {
    highlightedSatelliteInFov: highlightedSatelliteInFov,
    setHighlightedSatelliteInFov: setHighlightedSatelliteInFov,
    terminalDetails: tailDetailsPanelTerminal,
    isOverviewMode: isOverviewMode,
    satellitesInFOVIndexOffset: satellitesInFOVIndexOffset,
  };

  /*
   *
   * @param contractStartSecondsSinceEpoch  seconds since epoch as a number, or nil
   * @param contractEndSecondsSinceEpoch    seconds since epoch as a number, or nil
   * @returns If both values are defined, returns the contact start date fomatted as YYYYMMDD - YYYYMMDD
   */
  const getPeriodOfPerformanceString = (
    contractStartSecondsSinceEpoch: number | undefined | null,
    contractEndSecondsSinceEpoch: number | undefined | null,
  ) => {
    let periodOfPerformance: string = EMPTY_FIELD_STRING_PLACEHOLDER;

    // Only return the Period of Performance if both start and end are not null
    if (!isNil(contractStartSecondsSinceEpoch) && !isNil(contractEndSecondsSinceEpoch)) {
      const contractStartData = new Date(contractStartSecondsSinceEpoch! * SEC_TO_MS_CONVERSION);
      const contractEndData = new Date(contractEndSecondsSinceEpoch! * SEC_TO_MS_CONVERSION);
      periodOfPerformance = contractStartData
        .getFullYear()
        .toString()
        .concat(contractStartData.getMonth().toString().padStart(2, '0'))
        .concat(contractStartData.getDay().toString().padStart(2, '0'))
        .concat(' - ')
        .concat(contractEndData.getFullYear().toString())
        .concat(contractEndData.getMonth().toString().padStart(2, '0'))
        .concat(contractEndData.getDay().toString().padStart(2, '0'));
    }

    return periodOfPerformance;
  };

  const renderFullViewButton = () => {
    if (isFullTailViewMode) {
      return <div className="empty_button_spacer" />;
    }
    return detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE ? (
      <FullVehicleViewButton tailDetailsPanelTerminal={tailDetailsPanelTerminal} history={history} />
    ) : (
      <FulltailViewButton tailDetailsPanelTerminal={tailDetailsPanelTerminal} history={history} />
    );
  };

  const renderPanelButtons = () => {
    const showNextSatellitePanel =
      isNextSatellitePanelEnabled &&
      !isOverviewMode &&
      !isNil(tailDetailsPanelTerminal) &&
      !isNil(tailDetailsPanelTerminal.nextBeam) &&
      isPlatformTypeInList(tailDetailsPanelTerminal.platformType, TerminalPlatformsEnum.Airborne);
    return (
      <div>
        <React.Fragment>
          {showNextSatellitePanel ? <NextSatellitePanelContainerWrapper {...terminalProps} /> : null}
        </React.Fragment>
        {renderFullViewButton()}
      </div>
    );
  };

  const renderSignalQualityItem = () => {
    if (
      isOverviewMode ||
      !tailDetailsPanelTerminal ||
      isNil(tailDetailsPanelTerminal.flSignalQuality) ||
      !areAllowedRolesPresent(userData, Authorities.ROLE_SIGNAL_QUALITY_VIEW_ONLY)
    ) {
      return <></>;
    }
    return (
      <CollapseGroupItem
        id="signal_quality"
        label={'Signal Quality'}
        value={undefined}
        signalBars={
          <Tooltip
            title="Current satellite/antenna does not support Signal Quality"
            disableHoverListener={tailDetailsPanelTerminal.flSignalQuality !== -1}
          >
            <img
              src={signalQualitySvg(
                getSignalQuality(
                  tailDetailsPanelTerminal.connectivityStatus,
                  tailDetailsPanelTerminal.flSignalQuality,
                )!,
              )}
              alt="signal strength"
            />
          </Tooltip>
        }
        color={getConnectivityColor(tailDetailsPanelTerminal.connectivityStatus)}
      />
    );
  };

  const renderCurrentActivityGroup = () => (
    <CollapseGroup
      data-test="terminal__status__collapse__group__header--current__activity"
      collapseButtonClicked={() => {
        toggleGroupCollapsed('currentActivityGroupOpened');
      }}
      open={detailsPanelGroupCollapseState.currentActivityGroupOpened}
      title={
        <React.Fragment>
          {isMultiTerminalAircraft && !isOverviewMode && `${terminalTypeString} `}
          Activity
          {isOverviewMode && ' Summary'}
        </React.Fragment>
      }
    >
      <div className={fadedClassName}>
        {!isOverviewMode && <ConnectivityCollapseGroupItem terminal={tailDetailsPanelTerminal} />}
        {renderSignalQualityItem()}
        {isOverviewMode && (
          <React.Fragment>
            <ConnectivityCollapseGroupItem
              label="Ka Connection"
              terminal={getKaTerminalOnAircraft(tailDetailsPanelTerminal)}
            />
            <ConnectivityCollapseGroupItem
              label="Ku Connection"
              terminal={getKuTerminalOnAircraft(tailDetailsPanelTerminal)}
            />
          </React.Fragment>
        )}
        <CollapseGroupItem
          id="fl_rate"
          label={
            <React.Fragment>
              {isOverviewMode && 'Total '}
              FL Rate
            </React.Fragment>
          }
          value={tailDetailsPanelTerminal?.flDataRateBps}
          valueFormatter={bpsToUnitString}
        />
        <CollapseGroupItem
          id="rl_rate"
          label={
            <React.Fragment>
              {isOverviewMode && 'Total '}
              RL Rate
            </React.Fragment>
          }
          value={tailDetailsPanelTerminal?.rlDataRateBps}
          valueFormatter={bpsToUnitString}
        />
        {!isOverviewMode && (
          <CollapseGroupItem
            id="fl_sla"
            label="FL SLA"
            value={
              tailDetailsPanelTerminal
                ? formatSlaForDisplay(
                    tailDetailsPanelTerminal.flSlaLowerBoundBps,
                    tailDetailsPanelTerminal.flSlaUpperBoundBps,
                  )
                : null
            }
            hidden={false}
          />
        )}
        {!isOverviewMode && (
          <CollapseGroupItem
            id="rl_sla"
            label="RL SLA"
            value={
              tailDetailsPanelTerminal
                ? formatSlaForDisplay(
                    tailDetailsPanelTerminal.rlSlaLowerBoundBps,
                    tailDetailsPanelTerminal.rlSlaUpperBoundBps,
                  )
                : null
            }
            hidden={false}
          />
        )}
        <CollapseGroupItem
          id="fl_data_use"
          label={
            <React.Fragment>
              {isOverviewMode && 'Total '}
              FL Data Use
            </React.Fragment>
          }
          value={tailDetailsPanelTerminal?.flDataUseBytes}
          valueFormatter={bytesToUnitString}
        />
        <CollapseGroupItem
          id="rl_data_use"
          label={
            <React.Fragment>
              {isOverviewMode && 'Total '}
              RL Data Use
            </React.Fragment>
          }
          value={tailDetailsPanelTerminal?.rlDataUseBytes}
          valueFormatter={bytesToUnitString}
        />
        {!isOverviewMode && (
          <CollapseGroupItem
            id="ping_latency"
            label="Ping Latency"
            value={tailDetailsPanelTerminal?.pingAvgLatency}
            valueFormatter={toFixedValueFormatter(2, ' ms')}
          />
        )}
        {!isOverviewMode && (
          <CollapseGroupItem
            id="packet_loss"
            label="Packet Loss"
            value={tailDetailsPanelTerminal?.packetLossPercentage}
            valueFormatter={toFixedValueFormatter(2, '%')}
          />
        )}
        {!isOverviewMode && showDetailedConnectionStats && (
          <CollapseGroupItem
            id="jitter"
            label="Jitter"
            value={tailDetailsPanelTerminal?.jitter}
            valueFormatter={toFixedValueFormatter(2, ' ms')}
          />
        )}
      </div>
    </CollapseGroup>
  );

  const renderSituationalCapabilitiesGroup = () => (
    <CollapseGroup
      data-test="terminal__status__collapse__group__header--situational__capabilities"
      collapseButtonClicked={() => {
        toggleGroupCollapsed('situationalCapabilitiesGroupOpened');
      }}
      open={detailsPanelGroupCollapseState.situationalCapabilitiesGroupOpened}
      title="Situational Capabilities"
    >
      <div className={fadedClassName}>
        <CollapseGroupItem
          id="network"
          label="Network"
          value={tailDetailsPanelTerminal?.networkOrigin}
          valueFormatter={transformNetworkName}
        />
        <CollapseGroupItem
          id="hub"
          label="Hub"
          value={tailDetailsPanelTerminal?.hubName}
          truncate
          tooltipMaxWidth={HUB_NAME_TOOLTIP_MAX_WIDTH_PX}
        />
        <CollapseGroupItem
          id="beam"
          label="Beam"
          value={tailDetailsPanelTerminal?.beamDisplayName}
          truncate
          tooltipMaxWidth={BEAM_TOOLTIP_MAX_WIDTH_PX}
        />
        <CollapseGroupItem
          id="service_region"
          label="Service Region"
          value={tailDetailsPanelTerminal?.serviceRegionName}
          hidden={false}
          truncate
          tooltipMaxWidth={SERVICE_REGION_NAME_TOOLTIP_MAX_WIDTH_PX}
        />
        <CollapseGroupItem
          id="rl_waveform_type"
          label="RL Waveform Type"
          value={tailDetailsPanelTerminal?.rlWaveform}
          valueFormatter={transformRLWaveformName}
          hidden={false}
        />
        <CollapseGroupItem
          id="visibility_blanking"
          label="Visibility Blanking"
          value={tailDetailsPanelTerminal?.visibilityBlanking}
          valueFormatter={booleanToAliasOrPlaceholder('Yes', 'No')}
          hidden={false}
        />
      </div>
    </CollapseGroup>
  );

  const renderStatusGroup = () => {
    const isAirborne =
      !!tailDetailsPanelTerminal &&
      isPlatformTypeInList(tailDetailsPanelTerminal.platformType, TerminalPlatformsEnum.Airborne);
    const isNonAirborneTerminal = tailDetailsPanelTerminal && !isAirborne;
    return (
      <CollapseGroup
        data-test={
          isAirborne
            ? 'terminal__status__collapse__group__header--aircraft__status'
            : 'terminal__status__collapse__group__header--terminal__status'
        }
        collapseButtonClicked={() => {
          toggleGroupCollapsed('aircraftStatusGroupOpened');
        }}
        open={detailsPanelGroupCollapseState.aircraftStatusGroupOpened}
        title={isAirborne ? 'Aircraft Status' : 'Terminal Status'}
      >
        <div className={fadedClassName}>
          <CollapseGroupItem
            id="flight_phase"
            label="Flight Phase"
            value={tailDetailsPanelTerminal?.flightPhase}
            truncate
            tooltipMaxWidth={TAIL_DETAILS_TAIL_NUMBER_TOOLTIP_WIDTH_PX}
            hidden={isNonAirborneTerminal}
          />
          <CollapseGroupItem
            id="altitude"
            label="Altitude"
            value={tailDetailsPanelTerminal?.altitudeFeet}
            valueFormatter={toCommaSeparatedString(' ft')}
            truncate
            tooltipMaxWidth={ANTENNA_TOOLTIP_MAX_WIDTH_PX}
            hidden={isNonAirborneTerminal}
          />
          <CollapseGroupItem
            id="speed"
            label="Speed"
            value={tailDetailsPanelTerminal?.velocityKnots}
            truncate
            tooltipMaxWidth={MODEM_TOOLTIP_MAX_WIDTH_PX}
            valueFormatter={toFixedValueFormatter(0, ' knots')}
            hidden={isNonAirborneTerminal}
          />
          <CollapseGroupItem
            id="latitude"
            label="Latitude"
            value={tailDetailsPanelTerminal?.latitudeDeg}
            valueFormatter={toFixedValueFormatter(4, '°')}
          />
          <CollapseGroupItem
            id="longitude"
            label="Longitude"
            value={tailDetailsPanelTerminal?.longitudeDeg}
            valueFormatter={toFixedValueFormatter(4, '°')}
          />
        </div>
      </CollapseGroup>
    );
  };

  const renderTerminalConfigurationGroup = () => (
    <CollapseGroup
      data-test="terminal__status__collapse__group__header--tail__configuration"
      collapseButtonClicked={() => {
        toggleGroupCollapsed('tailConfigurationGroupOpened');
      }}
      open={detailsPanelGroupCollapseState.tailConfigurationGroupOpened}
      title={
        <React.Fragment>
          {isMultiTerminalAircraft && !isOverviewMode && `${terminalTypeString} `}
          Terminal Configuration
        </React.Fragment>
      }
    >
      <div className={fadedClassName}>
        <CollapseGroupItem
          id="antenna"
          label="Antenna"
          value={tailDetailsPanelTerminal?.antennaTypeName}
          truncate
          tooltipMaxWidth={ANTENNA_TOOLTIP_MAX_WIDTH_PX}
        />
        <CollapseGroupItem
          id="modem"
          label="Modem"
          value={tailDetailsPanelTerminal?.modemType}
          truncate
          tooltipMaxWidth={MODEM_TOOLTIP_MAX_WIDTH_PX}
        />
        <CollapseGroupItem id="sscfFileVer" label="SSCF" value={tailDetailsPanelTerminal?.sscfFileVer} />
        <CollapseGroupItem id="sedFileVer" label="SED" value={tailDetailsPanelTerminal?.sedFileVer} />
        <CollapseGroupItem
          id="viHnaPolicyVer"
          label="VI HNA Policy Version"
          value={tailDetailsPanelTerminal?.viHnaPolicyVersion}
        />
        <CollapseGroupItem id="terminal_sw" label="Terminal Software" value={tailDetailsPanelTerminal?.terminalSwVer} />
        <CollapseGroupItem
          id="bdt_version"
          label="BDT Version"
          value={tailDetailsPanelTerminal?.bdtVersion}
          hidden={isNil(tailDetailsPanelTerminal?.bdtVersion)}
        />
        <CollapseGroupItem
          id="clin"
          label="CLIN / Service Tier"
          value={
            tailDetailsPanelTerminal?.contractNumber && canManageServiceTier
              ? tailDetailsPanelTerminal?.contractNumber
              : null
          }
          hidden={!canManageServiceTier} // TODO: Update the role
        />
        <CollapseGroupItem
          id="periodOfPerformance"
          label="Period of Performance"
          value={
            canManageServiceTier
              ? getPeriodOfPerformanceString(
                  tailDetailsPanelTerminal?.contractStartSecondsSinceEpoch,
                  tailDetailsPanelTerminal?.contractEndSecondsSinceEpoch,
                )
              : null
          }
          hidden={!canManageServiceTier} // TODO: Update the role
        />
        <CollapseGroupItem
          id="requestedClinServiceTier"
          label="Requested CLIN / Service Tier"
          color="#9FAFBC"
          labelColor="#9FAFBC"
          value={
            canManageServiceTier && currentServiceTierChangeRequest?.requestedServiceTier
              ? currentServiceTierChangeRequest.requestedServiceTier
              : null
          }
          hidden={
            !canManageServiceTier ||
            isNil(currentServiceTierChangeRequest) ||
            currentServiceTierChangeRequest?.status === ServiceTierRequestStatus.COMPLETED ||
            currentServiceTierChangeRequest?.status === ServiceTierRequestStatus.DENIED
          } // TODO: Update the role
        />
      </div>
    </CollapseGroup>
  );

  const renderServiceTierChangeRequest = () =>
    canManageServiceTier ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingBottom: '54px',
          width: '357px',
        }}
      >
        <ServiceTierChangeRequestButton
          terminalDisplayName={tailDetailsPanelTerminal?.terminalDisplayName}
          contractNumber={tailDetailsPanelTerminal?.contractNumber}
          terminalId={tailDetailsPanelTerminal?.terminalUuid}
        />
        {requestingDeleteServiceTierChangeRequestError && currentServiceTierChangeRequest && (
          <CancelServiceTierChangeRequestErrorDialog
            open={!isNil(requestingDeleteServiceTierChangeRequestError)}
            onYesClicked={() => {
              dispatch(deleteServiceTierChangeRequest(currentServiceTierChangeRequest.id));
            }}
            onNoClicked={() => {
              dispatch(clearDeleteServiceTierChangeRequest());
            }}
          />
        )}
      </Box>
    ) : (
      <></>
    );

  return (
    <React.Fragment>
      <LookAnglesContainerWrapper {...terminalProps} />
      <SatellitesInFovContainer {...terminalProps} />
      {renderPanelButtons()}
      <CollapseGroupScrollbarContainer scrollbarHeightOffset={scrollbarHeightOffset}>
        {renderCurrentActivityGroup()}
        {!isOverviewMode && renderSituationalCapabilitiesGroup()}
        {(!isKuPlusKa || isOverviewMode) && renderStatusGroup()}
        {!isOverviewMode && renderTerminalConfigurationGroup()}
        {renderServiceTierChangeRequest()}
      </CollapseGroupScrollbarContainer>
    </React.Fragment>
  );
};

export default TerminalTabDataDefault;
